import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/dashboard/Sidebar';

export default function SyncLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [explaining, setExplaining] = useState(false);

  useEffect(() => { loadLogs(); }, [filter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?syncType=${filter}` : '';
      const res = await axios.get(`/api/sync/logs${params}`);
      setLogs(res.data);
    } catch (err) { /* error handled silently */ }
    finally { setLoading(false); }
  };

  const explainError = async (log) => {
    if (!log.error_message) return;
    setExplaining(true);
    setExplanation(null);
    try {
      const res = await axios.post('/api/sync/explain-error', {
        errorData: { sync_type: log.sync_type, error: log.error_message, metadata: log.metadata }
      });
      setExplanation(res.data.explanation);
    } catch {}
    finally { setExplaining(false); }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
            <div>
              <h2 className="page-title">Sync Logs</h2>
              <p className="page-sub">Full audit trail of every sync event</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="form-input" style={{ width: 160 }} value={filter} onChange={e => setFilter(e.target.value)}>
                <option value="all">All Types</option>
                <option value="vendor">Vendor</option>
                <option value="project">Project</option>
                <option value="invoice">Invoice</option>
                <option value="budget">Budget</option>
                <option value="payment_status">Payment Status</option>
              </select>
              <button className="btn btn-ghost btn-sm" onClick={loadLogs}>↻ Refresh</button>
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ gap: 16, alignItems: 'flex-start' }}>
          <div className="card card-glass" style={{ padding: 0 }}>
            {loading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : logs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">⇄</div>
                <h3 className="empty-state-title">No logs yet</h3>
                <p className="empty-state-desc">Sync activity will appear here</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Direction</th>
                      <th>Status</th>
                      <th>Records</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr
                        key={log.id}
                        onClick={() => { setSelected(log); setExplanation(null); }}
                        style={{ cursor: 'pointer', background: selected?.id === log.id ? 'var(--bg-card-hover)' : '' }}
                      >
                        <td>
                          <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>
                            {log.sync_type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          {log.direction.replace(/_/g, ' ')}
                        </td>
                        <td>
                          <span className={`badge badge-${statusColor(log.status)}`}>{log.status}</span>
                        </td>
                        <td>
                          <span style={{ color: 'var(--accent-green)' }}>{log.records_processed}</span>
                          {log.records_failed > 0 && <span style={{ color: 'var(--accent-red)', marginLeft: 4 }}>/ {log.records_failed} failed</span>}
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          {formatTime(log.started_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {selected ? (
            <div className="card card-glass">
              <div className="card-header">
                <span className="card-title" style={{ textTransform: 'capitalize' }}>
                  {selected.sync_type.replace(/_/g, ' ')} log detail
                </span>
                {selected.error_message && (
                  <button className="btn btn-ghost btn-sm" onClick={() => explainError(selected)} disabled={explaining}>
                    {explaining ? <><div className="spinner" style={{ width: 11, height: 11 }} /> Analyzing</> : '✦ Explain Error'}
                  </button>
                )}
              </div>

              {explanation && (
                <div className="alert alert-info" style={{ marginBottom: 16, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                  {explanation}
                </div>
              )}

              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <DetailRow label="Status" value={<span className={`badge badge-${statusColor(selected.status)}`}>{selected.status}</span>} />
                <DetailRow label="Direction" value={selected.direction.replace(/_/g, ' ')} />
                <DetailRow label="Records Processed" value={selected.records_processed} />
                <DetailRow label="Records Failed" value={selected.records_failed} />
                <DetailRow label="Started" value={new Date(selected.started_at).toLocaleString()} />
                <DetailRow label="Completed" value={selected.completed_at ? new Date(selected.completed_at).toLocaleString() : 'In progress'} />

                {selected.error_message && (
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>ERROR</div>
                    <div style={{
                      background: 'var(--accent-red-dim)',
                      border: '1px solid var(--accent-red)',
                      borderRadius: 'var(--radius-sm)',
                      padding: 12,
                      fontSize: 12,
                      fontFamily: 'var(--mono)',
                      color: 'var(--accent-red)',
                      wordBreak: 'break-all'
                    }}>
                      {selected.error_message}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card card-glass" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
              <div className="empty-state">
                <p className="empty-state-desc">Select a log to view details</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13 }}>{value}</span>
    </div>
  );
}

function statusColor(status) {
  return { success: 'green', error: 'red', pending: 'yellow', skipped: 'gray' }[status] || 'gray';
}

function formatTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}
